const condolivre_colors = {
	black: '#0C0F14',
	white: '#FFFFFF',
	gray_900: '#262C36',
	gray_700: '#6D7688',
	skyblue_900: '#0448AF',
	blue_600: '#0052CC',
	gray_200: '#ECF0F4',
	blue_300: '#6592e7',
	gray_800: '#474E5E',
	avocado_600: '#4D993A',
};

export interface LogoProps {
	standard?: string;
	custom?: string;
	width?: number;
	height?: number;
}

export interface NameProps {
	standard?: string;
	custom?: string;
}

export interface FooterProps {
	logo: LogoProps;
	text: string;
	cnpj: string;
	support_phone: string;
	whatsapp_phone: string;
	call_center_phone: string;
	address: {
		line_one: string;
		line_two: string;
	};
	colors: {
		text: string;
		subtitle: string;
		contact: string;
		background: string;
		containerBorderBottom: string;
		footerTextBorderBottom: string;
		line: string;
	};
	links: {
		visible: boolean;
		terms: string;
		privacy: string;
	};
}

export interface SidebarProps {
	logo: LogoProps;
	name: string;
	background?: string;
	active?: string;
	mobileBackground?: string;
}

export interface HeaderProps {
	user: string;
	company: string;
	icon: string;
	button: string;
	background: string;
}

export interface ThemeProps {
	primary: string;
	secondary: string;
	tertiary: string;
	accent: string;
}

export interface PagesProps {
	root: {
		title_stroke: {
			color: string;
			stroke: string;
		};
		box: {
			background: string;
			boxShadow: string;
		};
		tab: {
			color: string;
			active: string;
		};
		progress: {
			color: string;
			background: string;
		};
	};
	home?: {
		button: {
			variant: string;
			solid: boolean;
			color: string;
		};
	};
	register?: {
		button: {
			variant: string;
			solid: boolean;
			color: string;
		};
	};
	general?: {
		banner: {
			background: string;
			image: string;
		};
	};
	debit?: {
		banner: {
			background: string;
		};
		title: string;
		text: string;
		contact: {
			message: string;
			subtitle: string;
			phone: string;
		};
		how: {
			background: string;
			title: string;
			text: string;
			button: string;
		};
	};
	audit?: {
		logo: {
			image: string;
			background: string;
			width: number;
			height: number;
		};
		background: string;
		message: string;
		button: string;
	};
	documents?: {
		subtitle: string;
		message: string;
	};
	loans?: {
		banner: {
			background: string;
			image: string;
		};
	};
	simulation?: any;
}

export interface PartnerContextProps {
	logo?: LogoProps;
	name?: NameProps;
	favico?: string;
	footer?: FooterProps;
	sidebar?: SidebarProps;
	header?: HeaderProps;
	theme?: ThemeProps;
	pages?: PagesProps;
	gender: {
		the: string;
	};
	website?: string;
	pathname: string;
}

const generateDefaultContextProps = (
	primary: string,
	logo: LogoProps,
	name: NameProps,
	favico: string,
	footer_logo: LogoProps,
	sidebar_logo: LogoProps,
	website: string,
	pathname: string,
	text: string,
	cnpj: string,
	support_phone: string,
	whatsapp_phone: string,
	call_center_phone: string,
	gender: string,
): PartnerContextProps => ({
	logo,
	name,
	favico,
	footer: {
		logo: footer_logo,
		text,
		cnpj,
		support_phone,
		whatsapp_phone,
		call_center_phone,
		address: {
			line_one:
				'Alameda dos Jurupis, 1005, 7º Andar cj 71 a 74',
			line_two: 'Indianópolis, São Paulo - SP, 04088-003',
		},
		colors: {
			text: 'gray_700',
			subtitle: 'gray_800',
			contact: condolivre_colors.black,
			background: condolivre_colors.white,
			containerBorderBottom: '#0a0f41',
			footerTextBorderBottom: '#f1efef',
			line: condolivre_colors.white,
		},
		links: {
			visible: true,
			terms:
				'https://prod-condolivre-static-images.s3.sa-east-1.amazonaws.com/termos-e-condicoes.pdf',
			privacy:
				'https://prod-condolivre-static-images.s3.sa-east-1.amazonaws.com/aviso-privacidade.pdf',
		},
	},
	sidebar: {
		logo: sidebar_logo,
		name: 'Empréstimos',
		mobileBackground: '#4865d9',
	},
	header: {
		user: condolivre_colors.gray_900,
		company: condolivre_colors.gray_700,
		icon: condolivre_colors.skyblue_900,
		button: condolivre_colors.blue_600,
		background: '#161d4a',
	},
	theme: {
		primary,
		secondary: '#F05A22',
		tertiary: condolivre_colors.gray_200,
		accent: '#00ffff',
	},
	pages: {
		root: {
			title_stroke: {
				color: condolivre_colors.black,
				stroke: condolivre_colors.blue_300,
			},
			box: {
				background: condolivre_colors.white,
				boxShadow: '0px 4px 20px #6D76884D',
			},
			tab: {
				color: condolivre_colors.gray_700,
				active: condolivre_colors.gray_800,
			},
			progress: {
				color: condolivre_colors.black,
				background: condolivre_colors.gray_200,
			},
		},
		home: {
			button: {
				variant: 'ghost',
				solid: false,
				color: 'transparent',
			},
		},
		register: {
			button: {
				variant: 'primary',
				solid: true,
				color: '#0075D5',
			},
		},
		general: {
			banner: {
				background: '#4460CF',
				image: '#3658C3',
			},
		},
		debit: {
			banner: {
				background:
					'linear-gradient(90deg, rgba(101, 146, 231, 0.5) 1.37%, #0052cc 94.78%)',
			},
			title: condolivre_colors.black,
			text: condolivre_colors.gray_900,
			contact: {
				message: '#7c7c7c',
				subtitle: condolivre_colors.gray_800,
				phone: condolivre_colors.black,
			},
			how: {
				background: '#e9f2ff',
				title: condolivre_colors.blue_600,
				text: condolivre_colors.gray_900,
				button: condolivre_colors.blue_600,
			},
		},
		audit: {
			logo: {
				image: '/images/Logo-azul.png',
				background: condolivre_colors.blue_600,
				width: 314,
				height: 42,
			},
			background: '#4276df',
			message: condolivre_colors.black,
			button: condolivre_colors.avocado_600,
		},
		documents: {
			subtitle: condolivre_colors.gray_900,
			message: condolivre_colors.gray_700,
		},
		loans: {
			banner: {
				background: '#4460CF',
				image: '#3752c3',
			},
		},
		simulation: {
			gray_900: condolivre_colors.gray_900,
			gray_800: condolivre_colors.gray_800,
			gray_700: condolivre_colors.gray_700,
			black: condolivre_colors.black,
		},
	},
	gender: {
		the: gender,
	},
	website,
	pathname,
});

export const condolivre: PartnerContextProps = generateDefaultContextProps(
	'#4865d9',
	{
		standard: '/images/Logo_condolivre_pos.png',
		custom: '/images/Logo_condolivre_neg.png',
		width: 120,
		height: 31,
	},
	{
		standard: 'Condo Livre',
		custom: 'CondoLivre',
	},
	'/favicon.ico',
	{
		standard: '/images/Logo_condolivre_pos.png',
		custom: '/images/Logo_condolivre_neg.png',
		width: 200,
		height: 52,
	},
	{
		standard: '/images/Logo_condolivre_pos.png',
		custom: '/images/Logo_condolivre_neg.png',
		width: 200,
		height: 52,
	},
	'https://condolivre.com.br/',
	'cred',
	'A CondoLivre Análise de Crédito e Cobrança Ltda, inscrita sob o CNPJ 28.314.050/0001-62, é uma Fintech especializada em ',
	'soluções de crédito para o universo de condomínios.',
	'(11) 98084-1487',
	'5511980841487',
	'(011) 3097-9918',
	'a',
);

export const wohpag: PartnerContextProps = generateDefaultContextProps(
	'#80298F',
	{
		standard: '/images/Logo_wohpag_pos.png',
		custom: '/images/Logo_wohpag_neg.png',
		width: 186,
		height: 33,
	},
	{
		standard: 'Wohpag',
		custom: 'Wohpag',
	},
	'/favicon-wohpag.ico',
	{
		standard: '/images/footer_wohpag.png',
		custom: '/images/footer_wohpagneg.png',
		width: 214,
		height: 17,
	},
	{
		standard: '/images/sidebar_wohpag.png',
		custom: '/images/sidebar_wohpag.png',
		width: 66,
		height: 35,
	},
	'https://condolivre.com.br/',
	'wohpag',
	'Woh Instituição de Pagamentos Ltda. | Condolivre Análise de Crédito e Cobrança Ltda.',
	'CNPJ: 38.253.318/0001-57 | CNPJ: 29.314.050/0001-62',
	'(21) 96744-5464',
	'5521967445464',
	'(011) 3097-9918',
	'a',
);

export const partnerbank: PartnerContextProps = {
	logo: {
		standard: '/images/providers/partnerbank/logo_partnerbank_neg.png',
		custom: '/images/providers/partnerbank/logo_partnerbank_neg.png',
		width: 117,
		height: 43,
	},
	name: {
		standard: 'PartnerBank',
		custom: 'PartnerBank',
	},
	favico: '/images/providers/partnerbank/favicon.ico',
	footer: {
		logo: {
			standard: '/images/providers/partnerbank/footer_partnerbank_neg.png',
			custom: '/images/providers/partnerbank/footer_partnerbank_neg.png',
			width: 144,
			height: 66,
		},
		text: 'PartnerBank Instituição de Pagamentos S.A. | Condolivre Análise de Crédito e Cobrança Ltda.',
		cnpj: 'CNPJ: 28.979.546/0002-36 | CNPJ: 29.314.050/0001-62',
		support_phone: '(011) 98084-1487',
		whatsapp_phone: '5511980841487',
		call_center_phone: '(011) 3090-3227',
		address: {
			line_one:
				'Rua Paes Leme, 215, 14º andar - Pinheiros, São Paulo - SP, 05424-150',
			line_two: '',
		},
		colors: {
			text: 'white',
			subtitle: 'white',
			contact: condolivre_colors.white,
			background: 'linear-gradient(180deg, #091549 4%, #00539F 100%)',
			containerBorderBottom: '#0E1124',
			footerTextBorderBottom: '#0075D5',
			line: condolivre_colors.white,
		},
		links: {
			visible: false,
			terms:
				'https://prod-condolivre-static-images.s3.sa-east-1.amazonaws.com/termos-e-condicoes.pdf',
			privacy:
				'https://prod-condolivre-static-images.s3.sa-east-1.amazonaws.com/aviso-privacidade.pdf',
		},
	},
	sidebar: {
		logo: {
			standard: '/images/providers/partnerbank/sidebar_partnerbank.png',
			custom: '/images/providers/partnerbank/sidebar_partnerbank.png',
			width: 86,
			height: 32,
		},
		name: 'Consignado',
		background: '#010221',
		active: '#0075D5',
		mobileBackground: '#010221',
	},
	header: {
		user: condolivre_colors.white,
		company: condolivre_colors.white,
		icon: '#0075D5',
		button: '#01A8F7',
		background: 'transparent',
	},
	theme: {
		primary: '#0E1124',
		secondary: '#F05A22',
		tertiary: '#0E1124',
		accent: '#01A8F7',
	},
	pages: {
		root: {
			title_stroke: {
				color: condolivre_colors.white,
				stroke: '#0075D5',
			},
			box: {
				background: 'transparent',
				boxShadow: 'none',
			},
			tab: {
				color: condolivre_colors.white,
				active: condolivre_colors.white,
			},
			progress: {
				color: condolivre_colors.white,
				background: '#0E1124',
			},
		},
		home: {
			button: {
				variant: 'primary',
				solid: true,
				color: '#0075D5',
			},
		},
		register: {
			button: {
				variant: 'primary',
				solid: true,
				color: '#0075D5',
			},
		},
		general: {
			banner: {
				background:
					'linear-gradient(110deg, #0075D5 0%, #0E1124 40%, #0E1124 60%, #662D91 100%)',
				image: 'transparent',
			},
		},
		debit: {
			banner: {
				background:
					'linear-gradient(131deg, #0075D5 0%, #0E1124 20%, #0E1124 40%, #662D91 60%)',
			},
			title: condolivre_colors.white,
			text: condolivre_colors.white,
			contact: {
				message: condolivre_colors.white,
				subtitle: condolivre_colors.white,
				phone: condolivre_colors.white,
			},
			how: {
				background:
					'#0E1124 url("/images/providers/partnerbank/funcionamento.jpg") center center/cover no-repeat',
				title: condolivre_colors.white,
				text: condolivre_colors.white,
				button: '#2E8EFF',
			},
		},
		audit: {
			logo: {
				image: '/images/providers/partnerbank/logo_partnerbank_neg.png',
				background: '#010221',
				width: 117,
				height: 44,
			},
			background: '#0E1124',
			message: condolivre_colors.white,
			button: '#2E8EFF',
		},
		documents: {
			subtitle: condolivre_colors.white,
			message: condolivre_colors.white,
		},
		loans: {
			banner: {
				background:
					'linear-gradient(110deg, #0075D5 0%, #0E1124 40%, #0E1124 60%, #662D91 100%)',
				image: 'transparent',
			},
		},
		simulation: {
			gray_900: condolivre_colors.white,
			gray_800: condolivre_colors.white,
			gray_700: condolivre_colors.white,
			black: condolivre_colors.white,
		},
	},
	gender: {
		the: 'o',
	},
	website: 'https://www.partnerbank.com.br/',
	pathname: 'partnerbank',
};
